import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Building2, CreditCard, Shield, Settings2, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { usePayrollSettings, useUpdatePayrollSettings } from '@/api/queries/payroll.queries';

const INDIAN_STATES = [
  { value: 'MH', label: 'Maharashtra' }, { value: 'KA', label: 'Karnataka' },
  { value: 'DL', label: 'Delhi' }, { value: 'TN', label: 'Tamil Nadu' },
  { value: 'GJ', label: 'Gujarat' }, { value: 'WB', label: 'West Bengal' },
  { value: 'AP', label: 'Andhra Pradesh' }, { value: 'TG', label: 'Telangana' },
  { value: 'RJ', label: 'Rajasthan' }, { value: 'UP', label: 'Uttar Pradesh' },
  { value: 'MP', label: 'Madhya Pradesh' }, { value: 'HR', label: 'Haryana' },
  { value: 'PB', label: 'Punjab' }, { value: 'KL', label: 'Kerala' },
  { value: 'OR', label: 'Odisha' }, { value: 'BR', label: 'Bihar' },
  { value: 'AS', label: 'Assam' }, { value: 'JH', label: 'Jharkhand' },
  { value: 'CG', label: 'Chhattisgarh' }, { value: 'UK', label: 'Uttarakhand' },
];

const PT_NOTE = {
  MH: '₹175/month (salary ₹7,501–₹10,000) · ₹200/month (above ₹10,000)',
  KA: '₹150–₹200/month based on salary slab',
  WB: '₹110–₹200/month based on salary slab',
  GJ: '₹80–₹200/month based on salary slab',
  TN: '₹208/month (salary above ₹21,000)',
  AP: '₹150–₹200/month based on salary slab',
  TG: '₹150–₹200/month based on salary slab',
  DEFAULT: 'No Professional Tax in this state',
};

function Toggle({ on, onChange, label }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <div
        onClick={() => onChange(!on)}
        className={`relative w-9 h-5 rounded-full transition-colors ${on ? 'bg-[var(--accent)]' : 'bg-[var(--border)]'}`}>
        <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${on ? 'translate-x-4' : ''}`}/>
      </div>
      <span className="text-[13px] text-[var(--text-primary)]">{label}</span>
    </label>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="card p-5 space-y-4">
      <div className="flex items-center gap-2.5 pb-2 border-b border-[var(--border)]">
        <div className="w-8 h-8 rounded-[8px] bg-[var(--accent)]/10 flex items-center justify-center">
          <Icon size={16} className="text-[var(--accent)]"/>
        </div>
        <h3 className="text-[14px] font-bold text-[var(--text-primary)]">{title}</h3>
      </div>
      {children}
    </div>
  );
}

export default function PayrollBankSettingsPage() {
  const { data, isLoading } = usePayrollSettings();
  const { mutate, isPending } = useUpdatePayrollSettings();
  const [showAccNo, setShowAccNo] = useState(false);

  const [form, setForm] = useState({
    bank_name: '', account_number: '', ifsc_code: '',
    account_holder_name: '', account_type: 'CURRENT', branch: '',
    pf_number: '', esic_number: '', pt_number: '', tan_number: '',
    state: 'MH', payment_mode: 'BANK_TRANSFER', payment_day: 1,
    pf_applicable: true, esic_applicable: true, pt_applicable: true, tds_applicable: true,
    working_days_per_week: 6,
  });

  useEffect(() => {
    const d = data?.data;
    if (d) setForm(prev => ({ ...prev, ...d }));
  }, [data]);

  const set = (field, val) => setForm(prev => ({ ...prev, [field]: val }));

  const handleSave = () => {
    mutate(form, {
      onSuccess: () => toast.success('Payroll settings saved'),
      onError: () => toast.error('Failed to save settings'),
    });
  };

  const inputCls = "w-full h-10 px-3 bg-[var(--bg-muted)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]";
  const labelCls = "text-[12px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide";

  if (isLoading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[var(--accent)] border-t-transparent rounded-full animate-spin"/></div>;

  return (
    <div className="max-w-3xl space-y-5 fade-in">
      <PageHeader
        title="Payroll Bank & Statutory"
        subtitle="Company bank account for salary disbursement and statutory registration numbers"
        breadcrumb={[{ label: 'Settings', href: '/settings' }, { label: 'Payroll Bank & Statutory' }]}
      />

      {/* Alert: important note */}
      <div className="flex items-start gap-3 p-4 rounded-[12px] bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
        <AlertCircle size={16} className="text-amber-600 shrink-0 mt-0.5"/>
        <p className="text-[12px] text-amber-700 dark:text-amber-400">
          Bank account details are stored securely and printed on employee payslips. Make sure the account holder name matches your company's official bank account.
        </p>
      </div>

      {/* Company Bank Account */}
      <Section icon={CreditCard} title="Company Bank Account (for salary disbursement)">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className={labelCls}>Bank Name *</label>
            <input className={inputCls} placeholder="e.g. HDFC Bank, SBI" value={form.bank_name} onChange={e => set('bank_name', e.target.value)}/>
          </div>
          <div className="space-y-1.5">
            <label className={labelCls}>Account Holder Name *</label>
            <input className={inputCls} placeholder="As per bank records" value={form.account_holder_name} onChange={e => set('account_holder_name', e.target.value)}/>
          </div>
          <div className="space-y-1.5">
            <label className={labelCls}>Account Number *</label>
            <div className="relative">
              <input
                type={showAccNo ? 'text' : 'password'}
                className={`${inputCls} pr-10`}
                placeholder="Enter account number"
                value={form.account_number}
                onChange={e => set('account_number', e.target.value)}
              />
              <button type="button" onClick={() => setShowAccNo(!showAccNo)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)] hover:text-[var(--text-primary)]">
                {showAccNo ? <EyeOff size={15}/> : <Eye size={15}/>}
              </button>
            </div>
          </div>
          <div className="space-y-1.5">
            <label className={labelCls}>IFSC Code *</label>
            <input className={inputCls} placeholder="e.g. HDFC0001234" value={form.ifsc_code} onChange={e => set('ifsc_code', e.target.value.toUpperCase())}/>
          </div>
          <div className="space-y-1.5">
            <label className={labelCls}>Account Type</label>
            <select className={inputCls} value={form.account_type} onChange={e => set('account_type', e.target.value)}>
              <option value="CURRENT">Current Account</option>
              <option value="SAVINGS">Savings Account</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <label className={labelCls}>Branch</label>
            <input className={inputCls} placeholder="Branch name or city" value={form.branch} onChange={e => set('branch', e.target.value)}/>
          </div>
        </div>
      </Section>

      {/* Statutory Registrations */}
      <Section icon={Shield} title="Statutory Registration Numbers">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className={labelCls}>PF Registration Number</label>
            <input className={inputCls} placeholder="e.g. MH/MUM/12345" value={form.pf_number} onChange={e => set('pf_number', e.target.value.toUpperCase())}/>
            <p className="text-[11px] text-[var(--text-tertiary)]">Printed on payslips · From EPFO portal</p>
          </div>
          <div className="space-y-1.5">
            <label className={labelCls}>ESIC Registration Number</label>
            <input className={inputCls} placeholder="e.g. 12345678901" value={form.esic_number} onChange={e => set('esic_number', e.target.value)}/>
            <p className="text-[11px] text-[var(--text-tertiary)]">Printed on payslips · From ESIC portal</p>
          </div>
          <div className="space-y-1.5">
            <label className={labelCls}>TAN Number</label>
            <input className={inputCls} placeholder="e.g. MUMA12345A" value={form.tan_number} onChange={e => set('tan_number', e.target.value.toUpperCase())}/>
            <p className="text-[11px] text-[var(--text-tertiary)]">Tax Deduction Account Number · For TDS</p>
          </div>
          <div className="space-y-1.5">
            <label className={labelCls}>PT Registration Number</label>
            <input className={inputCls} placeholder="Professional Tax number" value={form.pt_number} onChange={e => set('pt_number', e.target.value)}/>
          </div>
        </div>
      </Section>

      {/* Payroll Configuration */}
      <Section icon={Settings2} title="Payroll Configuration">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className={labelCls}>Company State *</label>
            <select className={inputCls} value={form.state} onChange={e => set('state', e.target.value)}>
              {INDIAN_STATES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <p className="text-[11px] text-[var(--text-tertiary)]">{PT_NOTE[form.state] || PT_NOTE.DEFAULT}</p>
          </div>
          <div className="space-y-1.5">
            <label className={labelCls}>Payment Mode</label>
            <select className={inputCls} value={form.payment_mode} onChange={e => set('payment_mode', e.target.value)}>
              <option value="BANK_TRANSFER">Bank Transfer (NEFT/IMPS)</option>
              <option value="CASH">Cash</option>
              <option value="CHEQUE">Cheque</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <label className={labelCls}>Working Days Per Week</label>
            <select className={inputCls} value={form.working_days_per_week} onChange={e => set('working_days_per_week', Number(e.target.value))}>
              <option value={5}>5 Days (Mon–Fri)</option>
              <option value={6}>6 Days (Mon–Sat)</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <label className={labelCls}>Salary Payment Day</label>
            <select className={inputCls} value={form.payment_day} onChange={e => set('payment_day', Number(e.target.value))}>
              {[1,2,3,4,5,7,10,15].map(d => <option key={d} value={d}>Day {d} of every month</option>)}
            </select>
          </div>
        </div>

        {/* Statutory toggles */}
        <div className="pt-3 border-t border-[var(--border)]">
          <p className="text-[12px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide mb-3">Applicable Deductions</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Toggle on={form.pf_applicable} onChange={v => set('pf_applicable', v)} label="PF (EPF)"/>
            <Toggle on={form.esic_applicable} onChange={v => set('esic_applicable', v)} label="ESIC"/>
            <Toggle on={form.pt_applicable} onChange={v => set('pt_applicable', v)} label="Prof. Tax"/>
            <Toggle on={form.tds_applicable} onChange={v => set('tds_applicable', v)} label="TDS"/>
          </div>
        </div>
      </Section>

      <div className="flex justify-end pt-2">
        <Button onClick={handleSave} loading={isPending}>Save Settings</Button>
      </div>
    </div>
  );
}
